import { cn } from '@/lib/utils/cn'

export interface ViewToggleOption<V extends string = string> {
  value: V
  label: React.ReactNode
  icon?: React.ComponentType<{ className?: string; size?: number }>
}

export interface ViewToggleProps<V extends string = string> {
  options: ViewToggleOption<V>[]
  value: V
  onChange: (value: V) => void
  /** Accessible label for the group (screen readers). */
  'aria-label'?: string
  className?: string
}

/**
 * Segmented control for view-mode switching (Table / Summary, Grid / List /
 * Table, Table / Chart). 2–4 options per spec §View mode toggle; above that,
 * use a dropdown.
 */
export function ViewToggle<V extends string>({
  options,
  value,
  onChange,
  className,
  ...rest
}: ViewToggleProps<V>) {
  return (
    <div
      role="group"
      aria-label={rest['aria-label']}
      className={cn(
        'inline-flex gap-0.5 rounded-sm border border-border-default bg-surface p-0.5',
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value
        const Icon = opt.icon
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={cn(
              'inline-flex cursor-pointer items-center gap-1 rounded-xs border-none bg-transparent px-2.5 py-0.5 text-xs font-medium text-secondary transition-[background,color] duration-fast',
              'hover:text-primary',
              active && 'bg-muted text-primary',
            )}
          >
            {Icon ? <Icon size={12} /> : null}
            <span>{opt.label}</span>
          </button>
        )
      })}
    </div>
  )
}
